import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Calendar } from 'react-native-calendars';
import { format, differenceInCalendarDays } from 'date-fns';

const STATUS_FILTERS = ['All', 'Today', 'Upcoming', 'Pending', 'Cancelled'];

const BrandCalendar = ({ navigation }) => {
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markedDates, setMarkedDates] = useState({});
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const getStartOfToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const getCollabStatus = (collab) => {
    const today = getStartOfToday();
    const creditAvailableDate = new Date(collab.CreditAvailableStart);
    creditAvailableDate.setHours(0, 0, 0, 0);

    if (collab.Status === 'Cancelled') {
      return 'Cancelled';
    }

    const diffDays = differenceInCalendarDays(creditAvailableDate, today);

    if (diffDays === 0 && collab.Status === 'Accepted') {
      return 'Today';
    }

    return collab.Status === 'Accepted' ? 'Upcoming' : 'Pending';
  };

  const getCalendarStyleForDate = (collabsForDate, isToday) => {
    const statuses = collabsForDate.map((collab) => getCollabStatus(collab));

    let backgroundColor = '#DDF5E3';
    let borderColor = '#8FD19E';

    if (statuses.includes('Cancelled')) {
      backgroundColor = '#F7C6C6';
      borderColor = '#E08C8C';
    } else if (statuses.includes('Pending')) {
      backgroundColor = '#FDF05D';
      borderColor = '#D4C94A';
    } else if (statuses.includes('Today')) {
      backgroundColor = '#FDF05D';
      borderColor = '#111111';
    } else if (statuses.includes('Upcoming')) {
      backgroundColor = '#DDF5E3';
      borderColor = '#8FD19E';
    }

    return {
      customStyles: {
        container: {
          backgroundColor,
          borderRadius: 12,
          borderWidth: isToday ? 2 : 1.5,
          borderColor,
        },
        text: {
          color: '#111111',
          fontWeight: '800',
        },
      },
    };
  };

  useEffect(() => {
    fetch('https://collabbay.com/APIControllers/GetCollabCalendar.php', {
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message) {
          setCollabs([]);
          setMarkedDates({});
          setLoading(false);
          return;
        }

        const today = getStartOfToday();

        const futureCollabs = data.filter((collab) => {
          const visitDate = new Date(collab.CreditAvailableStart);
          visitDate.setHours(0, 0, 0, 0);
          return differenceInCalendarDays(visitDate, today) >= 0;
        });

        setCollabs(futureCollabs);

        const groupedByDate = {};
        futureCollabs.forEach((collab) => {
          const dateKey = format(new Date(collab.CreditAvailableStart), 'yyyy-MM-dd');
          if (!groupedByDate[dateKey]) {
            groupedByDate[dateKey] = [];
          }
          groupedByDate[dateKey].push(collab);
        });

        const dateMarks = {};
        Object.keys(groupedByDate).forEach((dateKey) => {
          const isToday = dateKey === format(today, 'yyyy-MM-dd');
          dateMarks[dateKey] = getCalendarStyleForDate(groupedByDate[dateKey], isToday);
        });

        setMarkedDates(dateMarks);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setLoading(false);
      });
  }, []);

  const filteredCollabs = useMemo(() => {
    return collabs.filter((collab) => {
      const derivedStatus = getCollabStatus(collab);
      const matchesFilter = activeFilter === 'All' || derivedStatus === activeFilter;

      const brand = `${collab.BrandName || ''}`.toLowerCase();
      const offer = `${collab.NonCreditOffer || ''}`.toLowerCase();
      const search = searchQuery.trim().toLowerCase();

      const matchesSearch =
        !search || brand.includes(search) || offer.includes(search);

      return matchesFilter && matchesSearch;
    });
  }, [collabs, activeFilter, searchQuery]);

  const summary = useMemo(() => {
    const todayCount = collabs.filter((c) => getCollabStatus(c) === 'Today').length;
    const upcoming = collabs.filter((c) => getCollabStatus(c) === 'Upcoming').length;
    const pending = collabs.filter((c) => getCollabStatus(c) === 'Pending').length;

    return { todayCount, upcoming, pending };
  }, [collabs]);

  const handleBackPress = () => {
    navigation.goBack();
  };

  const handleCardPress = (collabID) => {
    navigation.navigate('CollabCampaignDetailsPage', { CollabID: collabID });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix =
      day === 1 || day === 21 || day === 31
        ? 'st'
        : day === 2 || day === 22
        ? 'nd'
        : day === 3 || day === 23
        ? 'rd'
        : 'th';
    const month = format(date, 'MMMM');
    return `${day}${suffix} ${month}`;
  };

  const calculateDaysDifference = (dateString) => {
    const today = getStartOfToday();
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);

    const diffDays = differenceInCalendarDays(targetDate, today);

    if (diffDays === 0) return 'Today';
    return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="black" />
        <Text style={styles.loadingText}>Loading your collab calendar...</Text>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.container}>
          <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
            <Ionicons name="arrow-back" size={26} color="black" />
          </TouchableOpacity>

          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.heroCard}>
              <Text style={styles.headerTitle}>
                Collab Calendar<Text style={styles.headerDot}>.</Text>
              </Text>
              <Text style={styles.headerSubtitle}>
                Keep track of your upcoming collab visits and pending bookings.
              </Text>

              <View style={styles.summaryRow}>
                <View style={styles.summaryPill}>
                  <Text style={styles.summaryPillNumber}>{summary.todayCount}</Text>
                  <Text style={styles.summaryPillLabel}>Today</Text>
                </View>

                <View style={styles.summaryPill}>
                  <Text style={styles.summaryPillNumber}>{summary.upcoming}</Text>
                  <Text style={styles.summaryPillLabel}>Upcoming</Text>
                </View>

                <View style={styles.summaryPill}>
                  <Text style={styles.summaryPillNumber}>{summary.pending}</Text>
                  <Text style={styles.summaryPillLabel}>Pending</Text>
                </View>
              </View>
            </View>

            <View style={styles.calendarWrapper}>
              <View style={styles.sectionHeaderRow}>
                <Text style={styles.sectionTitle}>Calendar</Text>
                <Text style={styles.sectionHint}>Tap a coloured date</Text>
              </View>

              <Calendar
                markingType="custom"
                markedDates={markedDates}
                onDayPress={(day) => {
                  const selectedCollabs = collabs.filter(
                    (collab) =>
                      format(new Date(collab.CreditAvailableStart), 'yyyy-MM-dd') ===
                      day.dateString
                  );

                  if (selectedCollabs.length > 0) {
                    handleCardPress(selectedCollabs[0].CollabID);
                  }
                }}
                theme={{
                  calendarBackground: '#FFFFFF',
                  textSectionTitleColor: '#6B6B6B',
                  textSectionTitleDisabledColor: '#D1D1D1',
                  dayTextColor: '#111111',
                  textDisabledColor: '#D7D7D7',
                  monthTextColor: '#111111',
                  arrowColor: '#111111',
                  todayTextColor: '#111111',
                  textDayFontWeight: '700',
                  textMonthFontWeight: '800',
                  textDayHeaderFontWeight: '700',
                  textDayFontSize: 15,
                  textMonthFontSize: 18,
                  textDayHeaderFontSize: 12,
                }}
                style={styles.calendar}
                enableSwipeMonths={true}
              />
            </View>

            <View style={styles.legendRow}>
              <View style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: '#FDF05D', borderColor: '#D4C94A' }]} />
                <Text style={styles.legendText}>Pending / Today</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: '#DDF5E3', borderColor: '#8FD19E' }]} />
                <Text style={styles.legendText}>Upcoming</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: '#F7C6C6', borderColor: '#E08C8C' }]} />
                <Text style={styles.legendText}>Cancelled</Text>
              </View>
            </View>

            <View style={styles.filtersCard}>
              <Text style={styles.sectionTitle}>My Collab Visits</Text>
              <Text style={styles.crmSubtitle}>
                Before your visit: check the collab status and contact the brand.
              </Text>

              <View style={styles.searchWrap}>
                <Ionicons
                  name="search-outline"
                  size={18}
                  color="#666"
                  style={styles.searchIcon}
                />
                <TextInput
                  style={styles.searchInput}
                  placeholder="Filter by brand or offer"
                  placeholderTextColor="#8A8A8A"
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
              </View>

              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.filterRow}
              >
                {STATUS_FILTERS.map((filter) => {
                  const isActive = activeFilter === filter;
                  return (
                    <TouchableOpacity
                      key={filter}
                      style={[styles.filterChip, isActive && styles.filterChipActive]}
                      onPress={() => setActiveFilter(filter)}
                    >
                      <Text
                        style={[
                          styles.filterChipText,
                          isActive && styles.filterChipTextActive,
                        ]}
                      >
                        {filter}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>

            {filteredCollabs.length === 0 ? (
              <View style={styles.emptyStateCard}>
                <Ionicons name="calendar-outline" size={28} color="black" />
                <Text style={styles.noResultsTitle}>No matching collabs</Text>
                <Text style={styles.noResults}>
                  Try a different filter or search term.
                </Text>
              </View>
            ) : (
              filteredCollabs.map((collab, index) => {
                const collabStatus = getCollabStatus(collab);

                return (
                  <TouchableOpacity
                    key={index}
                    style={styles.card}
                    onPress={() => handleCardPress(collab.CollabID)}
                    activeOpacity={0.9}
                  >
                    <View style={styles.cardTopRow}>
                      <View style={styles.dateIconWrap}>
                        <Ionicons name="calendar-outline" size={20} color="black" />
                      </View>

                      <View style={styles.cardText}>
                        <Text style={styles.cardTitle}>
                          {formatDate(collab.CreditAvailableStart)}
                        </Text>
                        <Text style={styles.cardSubTitle}>
                          {calculateDaysDifference(collab.CreditAvailableStart)}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.statusBadge,
                          styles[`${collabStatus.toLowerCase()}Status`],
                        ]}
                      >
                        <Text style={styles.statusText}>{collabStatus}</Text>
                      </View>
                    </View>

                    <View style={styles.cardDivider} />

                    <View style={styles.cardDetails}>
                      <View style={styles.cardRow}>
                        <View style={styles.rowIconWrap}>
                          <Ionicons name="checkmark-circle" size={16} color="#111111" />
                        </View>
                        <Text style={styles.cardDetailText}>Status: {collab.Status}</Text>
                      </View>

                      <View style={styles.cardRow}>
                        <View style={styles.rowIconWrap}>
                          <Ionicons name="wallet-outline" size={16} color="#111111" />
                        </View>
                        <Text style={styles.cardDetailText}>Value: £{collab.Credits}</Text>
                      </View>

                      <View style={styles.cardRow}>
                        <View style={styles.rowIconWrap}>
                          <Ionicons name="briefcase-outline" size={16} color="#111111" />
                        </View>
                        <Text style={[styles.cardDetailText, styles.brandNameText]}>
                          {collab.BrandName}
                        </Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })
            )}
          </ScrollView>
        </View>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  safeArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  container: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 18,
    paddingTop: 14,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: '#555',
  },
  scrollViewContent: {
    paddingBottom: 34,
    paddingTop: 44,
  },
  backButton: {
    position: 'absolute',
    top: 12,
    left: 14,
    zIndex: 10,
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#F7F7F7',
    justifyContent: 'center',
    alignItems: 'center',
  },

  heroCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 28,
    padding: 22,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 29,
    fontWeight: '800',
    color: '#111111',
    marginBottom: 6,
  },
  headerDot: {
    color: '#111111',
  },
  headerSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: '#333333',
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  summaryPill: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.72)',
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 10,
    alignItems: 'center',
  },
  summaryPillNumber: {
    fontSize: 20,
    fontWeight: '800',
    color: '#111111',
    marginBottom: 2,
  },
  summaryPillLabel: {
    fontSize: 12,
    color: '#333333',
    fontWeight: '600',
  },

  calendarWrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#EEEEEE',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 3,
  },
  calendar: {
    width: '100%',
    borderRadius: 18,
    overflow: 'hidden',
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#111111',
  },
  sectionHint: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '600',
  },

  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
    gap: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F7F7F7',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  legendDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    marginRight: 8,
    borderWidth: 1.5,
  },
  legendText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#333',
  },

  filtersCard: {
    backgroundColor: '#F7F7F7',
    borderRadius: 28,
    padding: 18,
    marginBottom: 16,
  },
  crmSubtitle: {
    fontSize: 14,
    color: '#666666',
    lineHeight: 20,
    marginTop: 4,
    marginBottom: 14,
  },

  searchWrap: {
    position: 'relative',
    justifyContent: 'center',
    marginBottom: 14,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    zIndex: 2,
  },
  searchInput: {
    height: 46,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    paddingLeft: 42,
    paddingRight: 14,
    fontSize: 14,
    color: '#111111',
  },

  filterRow: {
    paddingRight: 6,
  },
  filterChip: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    marginRight: 8,
  },
  filterChipActive: {
    backgroundColor: '#111111',
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111111',
  },
  filterChipTextActive: {
    color: '#FFFFFF',
  },

  emptyStateCard: {
    backgroundColor: '#F7F7F7',
    borderRadius: 24,
    paddingVertical: 28,
    paddingHorizontal: 18,
    alignItems: 'center',
  },
  noResultsTitle: {
    marginTop: 10,
    fontSize: 18,
    fontWeight: '800',
    color: '#111111',
  },
  noResults: {
    marginTop: 6,
    fontSize: 14,
    textAlign: 'center',
    color: '#666666',
  },

  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    padding: 18,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#EEEEEE',
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 3,
  },
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateIconWrap: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: '#FDF05D',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardText: {
    flex: 1,
    paddingRight: 10,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#111111',
    marginBottom: 2,
  },
  cardSubTitle: {
    fontSize: 14,
    color: '#666666',
    fontWeight: '500',
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#F0F0F0',
    marginVertical: 14,
  },
  cardDetails: {
    gap: 10,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowIconWrap: {
    width: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  cardDetailText: {
    fontSize: 14,
    color: '#333333',
  },
  brandNameText: {
    fontWeight: '800',
    color: '#111111',
  },

  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    alignSelf: 'flex-start',
  },
  todayStatus: {
    backgroundColor: '#FDF05D',
  },
  upcomingStatus: {
    backgroundColor: '#DDF5E3',
  },
  pendingStatus: {
    backgroundColor: '#FDF05D',
  },
  cancelledStatus: {
    backgroundColor: '#F7C6C6',
  },
  statusText: {
    fontWeight: '800',
    fontSize: 12,
    color: '#111111',
  },
});

export default BrandCalendar;